import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';

import AdminLayout from '../../components/admin/AdminLayout';
import { useAuth } from '../../hooks/useAuth';
import {
  giftCardApi,
  type GiftCardAdminRow,
  type GiftCardAdminDetail,
  type GiftCardPromotionRow,
} from '../../services/giftCardApi';

export default function AdminGiftCards() {
  const { isAuthenticated, getToken } = useAuth();
  const [cards, setCards] = useState<GiftCardAdminRow[]>([]);
  const [promos, setPromos] = useState<GiftCardPromotionRow[]>([]);
  const [search, setSearch] = useState('');
  const [status, setStatus] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [detail, setDetail] = useState<GiftCardAdminDetail | null>(null);
  const [nextCursor, setNextCursor] = useState<string | null>(null);

  const load = async () => {
    if (!isAuthenticated) return;
    try {
      setLoading(true);
      const token = await getToken();
      if (!token) throw new Error('No access token');
      const [cardData, promoData] = await Promise.all([
        giftCardApi.adminListCards(token, {
          search: search || undefined,
          status: status || undefined,
        }),
        giftCardApi.adminListPromotions(token),
      ]);
      setCards(cardData.items);
      setNextCursor(cardData.nextCursor);
      setPromos(promoData);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load');
    } finally {
      setLoading(false);
    }
  };

  const loadMore = async () => {
    if (!nextCursor) return;
    try {
      const token = await getToken();
      if (!token) return;
      const data = await giftCardApi.adminListCards(token, {
        search: search || undefined,
        status: status || undefined,
        cursor: nextCursor,
      });
      setCards((prev) => [...prev, ...data.items]);
      setNextCursor(data.nextCursor);
    } catch (err) {
      alert(err instanceof Error ? err.message : 'Failed to load more');
    }
  };

  // Reload whenever the status filter changes so the list reflects it
  // immediately. Email/code search still requires the explicit Search button.
  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAuthenticated, status]);

  const handleVoid = async (id: string) => {
    if (!confirm('Void this gift card? This cannot be undone.')) return;
    try {
      const token = await getToken();
      if (!token) return;
      await giftCardApi.adminVoidCard(id, token);
      await load();
    } catch (err) {
      alert(err instanceof Error ? err.message : 'Failed to void');
    }
  };

  const handleResend = async (id: string) => {
    try {
      const token = await getToken();
      if (!token) return;
      await giftCardApi.adminResendCard(id, token);
      alert('Delivery email resent');
    } catch (err) {
      alert(err instanceof Error ? err.message : 'Failed to resend');
    }
  };

  const handleView = async (id: string) => {
    try {
      const token = await getToken();
      if (!token) return;
      setDetail(await giftCardApi.adminGetCard(id, token));
    } catch (err) {
      alert(err instanceof Error ? err.message : 'Failed to load detail');
    }
  };

  return (
    <AdminLayout
      title="Gift Cards"
      headerRight={
        <Link
          to="/admin/gift-cards/promotions/new"
          className="inline-flex items-center px-4 py-2 bg-primary text-white rounded-lg hover:bg-primary-dark"
        >
          + New Promotion
        </Link>
      }
    >
      {error && (
        <div className="mb-4 bg-red-50 border border-red-200 rounded-lg p-4 text-red-800">
          {error}
        </div>
      )}

      <h2 className="text-lg font-semibold mb-3">Promotions</h2>
      <div className="bg-white rounded-lg shadow-sm overflow-hidden mb-8">
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                Name
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                Trigger
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                Offers
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                Active
              </th>
              <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase">
                Actions
              </th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {promos.map((p) => (
              <tr key={p.id} className="hover:bg-gray-50">
                <td className="px-6 py-4">{p.name}</td>
                <td className="px-6 py-4">
                  {p.triggerType}
                  {p.code ? ` (${p.code})` : ''}
                </td>
                <td className="px-6 py-4 text-sm">
                  {p.tiers
                    .map((tr) => `pay $${tr.payAmount / 100}, get $${tr.faceValue / 100}`)
                    .join('; ')}
                </td>
                <td className="px-6 py-4">{p.isActive ? 'Yes' : 'No'}</td>
                <td className="px-6 py-4 text-right">
                  <Link to={`/admin/gift-cards/promotions/${p.id}/edit`} className="text-primary">
                    Edit
                  </Link>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <h2 className="text-lg font-semibold mb-3">Gift Cards</h2>
      <div className="mb-3 flex flex-wrap items-center gap-2">
        <input
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter') load();
          }}
          placeholder="Search code or email"
          className="px-3 py-2 border border-gray-300 rounded-lg w-72"
        />
        <button
          onClick={() => load()}
          className="px-4 py-2 rounded-lg border border-primary text-primary font-medium hover:bg-primary hover:text-white transition-colors"
        >
          Search
        </button>
        <select
          value={status}
          onChange={(e) => setStatus(e.target.value)}
          className="px-3 py-2 border border-gray-300 rounded-lg"
        >
          <option value="">All statuses</option>
          <option value="ACTIVE">Active</option>
          <option value="REDEEMED">Redeemed</option>
          <option value="PENDING_PAYMENT">Pending payment</option>
          <option value="VOID">Void</option>
        </select>
      </div>
      <div className="bg-white rounded-lg shadow-sm overflow-hidden">
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                Code
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                Value
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                Status
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                Purchaser
              </th>
              <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase">
                Actions
              </th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {cards.map((c) => (
              <tr key={c.id} className="hover:bg-gray-50">
                <td className="px-6 py-4 font-mono">{c.code}</td>
                <td className="px-6 py-4">${(c.faceValue / 100).toFixed(2)}</td>
                <td className="px-6 py-4">{c.status}</td>
                <td className="px-6 py-4">{c.purchaserEmail}</td>
                <td className="px-6 py-4 text-right space-x-3">
                  <button onClick={() => handleView(c.id)} className="text-gray-700">
                    View
                  </button>
                  {c.status === 'ACTIVE' && (
                    <button onClick={() => handleResend(c.id)} className="text-primary">
                      Resend
                    </button>
                  )}
                  {c.status !== 'REDEEMED' && c.status !== 'VOID' && (
                    <button onClick={() => handleVoid(c.id)} className="text-red-600">
                      Void
                    </button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {nextCursor && !loading && (
        <div className="mt-4 text-center">
          <button onClick={loadMore} className="px-4 py-2 bg-gray-100 rounded-lg">
            Load more
          </button>
        </div>
      )}
      {loading && <p className="mt-4 text-gray-500">Loading…</p>}

      {detail && (
        <div
          className="fixed inset-0 bg-black/40 flex items-center justify-center p-4"
          onClick={() => setDetail(null)}
        >
          <div
            className="bg-white rounded-lg max-w-lg w-full p-6 space-y-2"
            onClick={(e) => e.stopPropagation()}
          >
            <h3 className="text-lg font-semibold">Gift card {detail.code}</h3>
            <p>
              Value: ${(detail.faceValue / 100).toFixed(2)} · Paid: $
              {(detail.paidAmount / 100).toFixed(2)} · Status: {detail.status}
            </p>
            <p>Purchaser: {detail.purchaserEmail}</p>
            <p>Created: {new Date(detail.createdAt).toLocaleString()}</p>
            {detail.isGift && (
              <p>
                Recipient: {detail.recipientEmail} ({detail.recipientName})
              </p>
            )}
            {detail.promotion && (
              <p>
                Promotion: {detail.promotion.name}
                {detail.promotion.code ? ` (${detail.promotion.code})` : ''}
              </p>
            )}
            {detail.redeemer && (
              <p>
                Redeemed by: {detail.redeemer.email}
                {detail.redeemedAt ? ` on ${new Date(detail.redeemedAt).toLocaleString()}` : ''}
              </p>
            )}
            <button
              onClick={() => setDetail(null)}
              className="mt-2 px-4 py-2 bg-gray-100 rounded-lg"
            >
              Close
            </button>
          </div>
        </div>
      )}
    </AdminLayout>
  );
}
